import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, FolderTree, ShoppingCart, Users, MailQuestion, Settings, Menu, LogOut, Store, Palette, Star, SlidersHorizontal, Image as ImageIcon, Tag, Paintbrush, RotateCcw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: FolderTree },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/returns", label: "Returns", icon: RotateCcw },
  { to: "/admin/campaigns", label: "Campaigns", icon: Tag },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/enquiries", label: "Enquiries", icon: MailQuestion },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/website-control", label: "Website Control", icon: SlidersHorizontal },
  { to: "/admin/editor", label: "Visual Editor", icon: Paintbrush },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/theme", label: "Theme", icon: Palette },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function NavItems({ onClick }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-300 ${isActive ? "bg-gold text-[#111] font-medium" : "text-[#A3A3A3] hover:text-[#FAF9F6] hover:bg-white/5"}`}
          data-testid={`admin-nav-${n.label.toLowerCase()}`}>
          <n.icon strokeWidth={1.5} size={18} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === false) { navigate("/admin-login"); return null; }
  if (user && user.role !== "admin") { navigate("/"); return null; }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F6] md:grid md:grid-cols-[240px_1fr]" data-testid="admin-layout">
      <aside className="hidden md:flex flex-col bg-[#111111] min-h-screen sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <p className="font-serif text-lg text-[#FAF9F6]">HUMAIRA <span className="text-gold">CRAFTS</span></p>
          <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-1">Admin Panel</p>
        </div>
        <div className="flex-1 py-4"><NavItems /></div>
        <div className="p-4 border-t border-white/10 flex flex-col gap-1">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 px-4 py-2 text-sm text-[#A3A3A3] hover:text-[#FAF9F6] transition-colors duration-300" data-testid="admin-view-store">
            <Store strokeWidth={1.5} size={18} /> View Store
          </button>
          <button onClick={logout} className="flex items-center gap-3 px-4 py-2 text-sm text-[#A3A3A3] hover:text-[#FAF9F6] transition-colors duration-300" data-testid="admin-logout">
            <LogOut strokeWidth={1.5} size={18} /> Logout
          </button>
        </div>
      </aside>

      <div className="md:hidden bg-[#111111] text-[#FAF9F6] flex items-center justify-between px-4 h-14 sticky top-0 z-40">
        <p className="font-serif">HUMAIRA <span className="text-gold">CRAFTS</span> <span className="text-[10px] text-[#A3A3A3] uppercase tracking-widest ml-1">Admin</span></p>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button aria-label="Menu" data-testid="admin-mobile-menu"><Menu strokeWidth={1.5} size={22} /></button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-[#111111] w-72 border-white/10">
            <div className="mt-8"><NavItems onClick={() => setOpen(false)} /></div>
            <div className="mt-6 border-t border-white/10 pt-4">
              <button onClick={() => navigate("/")} className="flex items-center gap-3 px-4 py-2 text-sm text-[#A3A3A3]" data-testid="admin-view-store-mobile"><Store strokeWidth={1.5} size={18} /> View Store</button>
              <button onClick={logout} className="flex items-center gap-3 px-4 py-2 text-sm text-[#A3A3A3]" data-testid="admin-logout-mobile"><LogOut strokeWidth={1.5} size={18} /> Logout</button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <main className="p-4 md:p-10 max-w-6xl w-full mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
